// V5.45 : Mission Data Manager - Architecture "Silos Étanches" avec pack_type
// Source de vérité unique pour toutes les données de mission
// Format: { id: "EFF-TIMESTAMP-RANDOM", pack_type: "demenagement", step1_adresses: {...}, categories: {...} }
// V5.45 : Clé de stockage = effaceur_mission_{pack_type}_{mission_id}

// ===================================================================
// V5.45 : TYPES DE PACK VALIDES
// ===================================================================

export const VALID_PACK_TYPES = ['demenagement', 'succession', 'audit'];

// ===================================================================
// V5.26 : GÉNÉRATION D'ID DE SESSION UNIQUE
// ===================================================================

export const generateMissionId = () => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  const timestamp = `${day}${month}${year}`;
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `EFF-${timestamp}-${random}`;
};

// ===================================================================
// V5.26 : STRUCTURE DE DONNÉES CIBLE (SILOS ÉTANCHES)
// ===================================================================

// Structure initiale d'une catégorie
const createEmptyCategoryData = () => ({
  status: 'none', // 'active' | 'desactive' | 'none'
  provider: '',
  data: {}
});

// Structure initiale complète d'une mission
// V5.45 : Ajout de pack_type pour isolation des silos + email obligatoire
export const createEmptyMissionData = (missionId = null, userData = null, packType = 'demenagement') => ({
  id: missionId || generateMissionId(),
  pack_type: packType, // V5.45 : Type de pack pour silo étanche
  created_at: new Date().toISOString(),
  // V5.45 : Fil conducteur - données client avec first_name/last_name séparés + email
  first_name: userData?.first_name || '',
  last_name: userData?.last_name || '',
  telephone: userData?.phone || '',
  email: userData?.email || '', // V5.45 : Email mappé dès l'ouverture
  client: {
    first_name: userData?.first_name || '',
    last_name: userData?.last_name || '',
    nom_prenom: userData ? `${userData.first_name || ''} ${userData.last_name || ''}`.trim() : '', // Compatibilité
    telephone: userData?.phone || '',
    email: userData?.email || ''
  },
  step1_adresses: {
    depart: {
      adresse: '',
      code_postal: '',
      ville: ''
    },
    arrivee: {
      adresse: '',
      code_postal: '',
      ville: ''
    },
    date_demenagement: ''
  },
  categories: {
    electricite: { 
      status: 'none', 
      provider: '', 
      data: { pdl: '', index: null } 
    },
    gaz: { 
      status: 'none', 
      provider: '', 
      data: { pce: '', index: null } 
    },
    eau: { 
      status: 'none', 
      provider: '', 
      data: { numContrat: '', index: null, unknownNumber: false } 
    },
    internet: { 
      status: 'none', 
      provider: '', 
      data: { rio: '' } 
    },
    assurance: { 
      status: 'none', 
      provider: '', 
      data: { numContrat: '' } 
    },
    mutuelle: { 
      status: 'none', 
      provider: '', 
      data: { numAdherent: '' } 
    },
    banque: { 
      status: 'none', 
      provider: '', 
      data: { numCompte: '' } 
    },
    sport: { 
      status: 'none', 
      provider: '', 
      data: { nomEnseigne: '', numAdherent: '' } 
    },
    presse: { 
      status: 'none', 
      provider: '', 
      data: { titre: '', numAbonne: '' } 
    }
  },
  consent_fee_disclaimer: false,
  current_step: 1,
  justificatif_domicile: null,
  deferred_fields: [],
  signed_at: null
});

// ===================================================================
// V5.45 : CLÉS DE STOCKAGE AVEC PACK_TYPE (SILOS ÉTANCHES)
// ===================================================================

// V5.45 : Nouvelle clé incluant pack_type pour isolation totale
const getStorageKey = (packType, missionId) => `effaceur_mission_${packType}_${missionId}`;
const getActiveMissionKey = (packType) => `effaceur_active_mission_${packType}`;

// V5.45 : Récupérer toutes les missions actives par pack_type
export const getActiveMissionByPackType = (packType) => {
  const activeMissionId = localStorage.getItem(getActiveMissionKey(packType));
  return activeMissionId;
};

// ===================================================================
// V5.45 : PROTOCOLE "CLEAN SLATE" - NETTOYAGE PAR PACK_TYPE
// ===================================================================

// V5.45 : Nettoyage ciblé pour un pack_type spécifique (sans toucher aux autres silos)
export const cleanSlateProtocol = (packType = null, newMissionId = null) => {
  console.log(`🧹 V5.45 Clean Slate Protocol - Nettoyage pour pack_type: ${packType || 'ALL'}`);
  
  const keysToRemove = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (
      // Anciennes clés V5.25 et antérieures (toujours supprimer)
      key === 'currentMission' ||
      key === 'missionData' ||
      key === 'currentStep' ||
      key === 'formData' ||
      key === 'strategyChoices' ||
      key === 'technicalData' ||
      key === 'mission_current_step' ||
      key === 'mission_demenagement_data' ||
      key === 'mission_strategy_choices' ||
      key === 'mission_custom_contracts' ||
      key === 'active_mission_id' ||
      key === 'effaceur_active_mission_id' || // Ancienne clé globale
      // Anciennes sessions
      key.startsWith('mission_') ||
      key.startsWith('effaceur_session_') ||
      // V5.45 : Si pack_type spécifié, ne supprimer QUE les clés de ce pack
      (packType && key.startsWith(`effaceur_mission_${packType}_`) && newMissionId && !key.includes(newMissionId)) ||
      (packType && key === `effaceur_active_mission_${packType}` && newMissionId) ||
      // Si pas de pack_type, supprimer toutes les anciennes clés sans pack_type
      (!packType && key.startsWith('effaceur_mission_') && !key.match(/effaceur_mission_(demenagement|succession|audit)_/))
    )) {
      keysToRemove.push(key);
    }
  }
  
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    console.log(`🧹 Suppression clé: ${key}`);
  });
  
  console.log(`🧹 ${keysToRemove.length} clés supprimées`);
  return keysToRemove.length;
};

// Fonction exportée pour le Dashboard
export const clearPreviousData = cleanSlateProtocol;

// ===================================================================
// V5.27 : RESET TOTAL - NETTOYAGE COMPLET POUR TESTS (Admin)
// ===================================================================

export const resetTotalV527 = () => {
  console.log('🔄 V5.27 RESET TOTAL - Purge complète localStorage & sessionStorage');
  
  const keysToRemove = [];
  const keysToPreserve = ['token', 'user']; // Préserver l'authentification
  
  // 1. Purger toutes les clés de mission du localStorage
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key && !keysToPreserve.includes(key)) {
      // Supprimer toutes les clés liées aux missions
      if (
        key === 'currentMission' ||
        key === 'missionData' ||
        key === 'currentStep' ||
        key === 'formData' ||
        key === 'strategyChoices' ||
        key === 'technicalData' ||
        key === 'mission_current_step' ||
        key === 'mission_demenagement_data' ||
        key === 'mission_strategy_choices' ||
        key === 'mission_custom_contracts' ||
        key === 'active_mission_id' ||
        key.startsWith('mission_') ||
        key.startsWith('effaceur_session_') ||
        key.startsWith('effaceur_mission_') ||
        key.startsWith('effaceur_active_')
      ) {
        keysToRemove.push(key);
      }
    }
  }
  
  // Supprimer les clés identifiées
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    console.log(`🔄 V5.27 Reset - Suppression: ${key}`);
  });
  
  // 2. Purger sessionStorage (tout sauf auth)
  const sessionKeysToRemove = [];
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (key && !keysToPreserve.includes(key)) {
      sessionKeysToRemove.push(key);
    }
  }
  sessionKeysToRemove.forEach(key => {
    sessionStorage.removeItem(key);
    console.log(`🔄 V5.27 Reset Session - Suppression: ${key}`);
  });
  
  // 3. Réinitialiser l'ID de mission active
  localStorage.removeItem('effaceur_active_mission_id');
  
  console.log(`✅ V5.27 Reset complet: ${keysToRemove.length} clés localStorage + ${sessionKeysToRemove.length} clés sessionStorage supprimées`);
  
  return {
    localStorageCleared: keysToRemove.length,
    sessionStorageCleared: sessionKeysToRemove.length,
    total: keysToRemove.length + sessionKeysToRemove.length
  };
};

// ===================================================================
// V5.45 : PERSISTENCE SYNC PERMANENTE AVEC PACK_TYPE
// ===================================================================

export const saveMissionData = (missionData) => {
  if (!missionData?.id) {
    console.error('❌ saveMissionData: ID de mission manquant');
    return false;
  }
  
  // V5.45 : pack_type obligatoire pour isolation
  const packType = missionData.pack_type || 'demenagement';
  
  try {
    const key = getStorageKey(packType, missionData.id);
    const dataToSave = {
      ...missionData,
      pack_type: packType, // S'assurer que pack_type est toujours présent
      updated_at: new Date().toISOString()
    };
    
    localStorage.setItem(key, JSON.stringify(dataToSave));
    localStorage.setItem(getActiveMissionKey(packType), missionData.id);
    
    console.log(`💾 V5.45 Sync: Mission ${missionData.id} (${packType}) sauvegardée`);
    return true;
  } catch (e) {
    console.error('❌ Erreur sauvegarde mission:', e);
    return false;
  }
};

// V5.45 : Chargement avec pack_type obligatoire
export const loadMissionData = (missionId = null, packType = 'demenagement') => {
  if (!missionId) {
    // V5.45 : Charger la mission active du pack_type spécifié uniquement
    missionId = localStorage.getItem(getActiveMissionKey(packType));
    if (!missionId) {
      console.log(`ℹ️ V5.45: Pas de mission active pour pack_type: ${packType}`);
      return null;
    }
  }
  
  try {
    const key = getStorageKey(packType, missionId);
    const saved = localStorage.getItem(key);
    
    if (saved) {
      const data = JSON.parse(saved);
      console.log(`📂 V5.45 Load: Mission ${missionId} (${packType}) chargée`);
      return data;
    }
    
    // V5.45 : Fallback - chercher dans les anciennes clés (migration)
    const oldKey = `effaceur_mission_${missionId}`;
    const oldSaved = localStorage.getItem(oldKey);
    if (oldSaved) {
      const data = JSON.parse(oldSaved);
      console.log(`📂 V5.45 Migration: Ancienne mission ${missionId} trouvée, migration vers nouveau format`);
      // Migrer vers le nouveau format
      data.pack_type = packType;
      saveMissionData(data);
      localStorage.removeItem(oldKey); // Supprimer l'ancienne clé
      return data;
    }
    
    return null;
  } catch (e) {
    console.error('❌ Erreur chargement mission:', e);
    return null;
  }
};

// V5.45 : Récupérer l'ID de mission active pour un pack_type
export const getActiveMissionId = (packType = 'demenagement') => {
  return localStorage.getItem(getActiveMissionKey(packType));
};

// ===================================================================
// V5.26 : MISE À JOUR DE CATÉGORIE (Étape 2)
// ===================================================================

export const updateCategoryStatus = (missionData, categoryId, status, provider = '') => {
  if (!missionData?.categories?.[categoryId]) {
    console.error(`❌ Catégorie inconnue: ${categoryId}`);
    return missionData;
  }
  
  const updatedData = {
    ...missionData,
    categories: {
      ...missionData.categories,
      [categoryId]: {
        ...missionData.categories[categoryId],
        status: status, // 'active' | 'desactive' | 'none'
        provider: provider
      }
    }
  };
  
  // Sync permanente
  saveMissionData(updatedData);
  
  return updatedData;
};

// ===================================================================
// V5.26 : MISE À JOUR DES DONNÉES TECHNIQUES (Étape 3)
// ===================================================================

export const updateCategoryTechnicalData = (missionData, categoryId, technicalData) => {
  if (!missionData?.categories?.[categoryId]) {
    console.error(`❌ Catégorie inconnue: ${categoryId}`);
    return missionData;
  }
  
  const updatedData = {
    ...missionData,
    categories: {
      ...missionData.categories,
      [categoryId]: {
        ...missionData.categories[categoryId],
        data: {
          ...missionData.categories[categoryId].data,
          ...technicalData
        }
      }
    }
  };
  
  // Sync permanente
  saveMissionData(updatedData);
  
  return updatedData;
};

// ===================================================================
// V5.26 : FILTRE ACTIF (Mapping Étape 2 → Étape 3)
// ===================================================================

export const getActiveCategories = (missionData) => {
  if (!missionData?.categories) return [];
  
  const activeCategories = [];
  
  Object.entries(missionData.categories).forEach(([categoryId, categoryData]) => {
    // N'inclure QUE les catégories où status !== 'none'
    if (categoryData.status && categoryData.status !== 'none') {
      activeCategories.push({
        id: categoryId,
        status: categoryData.status,
        provider: categoryData.provider,
        data: categoryData.data
      });
    }
  });
  
  return activeCategories;
};

// ===================================================================
// V5.26 : MISE À JOUR DES ADRESSES (Étape 1)
// ===================================================================

export const updateAddresses = (missionData, adresses) => {
  const updatedData = {
    ...missionData,
    step1_adresses: {
      ...missionData.step1_adresses,
      ...adresses
    }
  };
  
  saveMissionData(updatedData);
  return updatedData;
};

// ===================================================================
// V5.26 : MISE À JOUR DE L'ÉTAPE COURANTE
// ===================================================================

export const updateCurrentStep = (missionData, step) => {
  const updatedData = {
    ...missionData,
    current_step: step
  };
  
  saveMissionData(updatedData);
  return updatedData;
};

// ===================================================================
// V5.26-C : CONSENTEMENT DÉCHARGE DE RESPONSABILITÉ
// ===================================================================

export const updateFeeDisclaimerConsent = (missionData, consent) => {
  const updatedData = {
    ...missionData,
    consent_fee_disclaimer: consent
  };
  
  saveMissionData(updatedData);
  return updatedData;
};

// ===================================================================
// V5.26 : PRÉPARATION DES DONNÉES POUR LE BACKEND/PDF
// ===================================================================

export const prepareDataForBackend = (missionData) => {
  if (!missionData) return null;
  
  // Convertir la structure V5.26 en format attendu par le backend
  const formData = {
    ancienne_adresse: missionData.step1_adresses?.depart?.adresse || '',
    ancienne_code_postal: missionData.step1_adresses?.depart?.code_postal || '',
    ancienne_ville: missionData.step1_adresses?.depart?.ville || '',
    nouvelle_adresse: missionData.step1_adresses?.arrivee?.adresse || '',
    nouvelle_code_postal: missionData.step1_adresses?.arrivee?.code_postal || '',
    nouvelle_ville: missionData.step1_adresses?.arrivee?.ville || '',
    date_demenagement: missionData.step1_adresses?.date_demenagement || ''
  };
  
  // Préparer les choix de stratégie
  const strategyChoices = {};
  const technicalData = {};
  const providers = {};
  
  Object.entries(missionData.categories || {}).forEach(([categoryId, categoryData]) => {
    if (categoryData.status !== 'none') {
      // Strategy choice format attendu
      strategyChoices[categoryId] = {
        action: categoryData.status === 'active' ? 'transfer' : 'liquidate',
        provider: categoryData.provider || ''
      };
      
      // Provider
      providers[categoryId] = categoryData.provider || '';
      technicalData[`${categoryId}_provider`] = categoryData.provider || '';
      
      // Données techniques spécifiques
      if (categoryData.data) {
        Object.entries(categoryData.data).forEach(([key, value]) => {
          // Mapper les clés vers le format backend
          const backendKey = mapTechnicalKeyToBackend(categoryId, key);
          if (backendKey && value) {
            technicalData[backendKey] = value;
          }
        });
      }
    }
  });
  
  return {
    formData,
    strategyChoices,
    technicalData,
    providers,
    consentFeeDisclaimer: missionData.consent_fee_disclaimer || false
  };
};

// Mapping des clés techniques V5.26 → Backend
const mapTechnicalKeyToBackend = (categoryId, key) => {
  const mapping = {
    electricite: { pdl: 'pdl', index: 'index_electricite' },
    gaz: { pce: 'pce', index: 'index_gaz' },
    eau: { numContrat: 'water_contract', index: 'index_eau', unknownNumber: 'eau_unknown_number' },
    internet: { rio: 'rio' },
    assurance: { numContrat: 'contract_assurance' },
    mutuelle: { numAdherent: 'mutuelle_number' },
    banque: { numCompte: 'bank_account_id' },
    sport: { nomEnseigne: 'sport_name', numAdherent: 'sport_member_id' },
    presse: { titre: 'presse_title', numAbonne: 'presse_subscriber_id' }
  };
  
  return mapping[categoryId]?.[key] || null;
};

// ===================================================================
// V5.26 : VALIDATION MINIMALE
// ===================================================================

export const validateStep1 = (missionData) => {
  const adresses = missionData?.step1_adresses;
  return !!(
    adresses?.depart?.adresse &&
    adresses?.arrivee?.adresse &&
    adresses?.date_demenagement
  );
};

export const validateStep2 = (missionData) => {
  // Au moins une catégorie sélectionnée (status !== 'none')
  const activeCategories = getActiveCategories(missionData);
  
  // Vérifier que tous les providers sont renseignés
  for (const cat of activeCategories) {
    if (!cat.provider || cat.provider.trim() === '') {
      return { valid: false, missingProvider: cat.id };
    }
  }
  
  return { valid: activeCategories.length > 0, missingProvider: null };
};

// V5.26 : Validation souple Étape 3 - seul le RIO est critique
export const validateStep3 = (missionData) => {
  const activeCategories = getActiveCategories(missionData);
  const warnings = [];
  let canProceed = true;
  
  for (const cat of activeCategories) {
    // RIO : seule validation critique (12 caractères)
    if (cat.id === 'internet' && cat.data?.rio) {
      const rio = cat.data.rio;
      if (rio && !/^[A-Z0-9]{12}$/i.test(rio)) {
        canProceed = false;
        warnings.push('Le RIO doit contenir exactement 12 caractères alphanumériques');
      }
    }
    
    // PDL/PCE : warning si < 14 caractères
    if (cat.id === 'electricite' && cat.data?.pdl) {
      if (cat.data.pdl.length > 0 && cat.data.pdl.length < 14) {
        warnings.push('Le PDL devrait contenir au moins 14 caractères');
      }
    }
    if (cat.id === 'gaz' && cat.data?.pce) {
      if (cat.data.pce.length > 0 && cat.data.pce.length < 14) {
        warnings.push('Le PCE devrait contenir au moins 14 caractères');
      }
    }
  }
  
  return { canProceed, warnings };
};

// V5.26-C : Vérifier le consentement avant paiement
export const validateBeforePayment = (missionData) => {
  return missionData?.consent_fee_disclaimer === true;
};

export default {
  // V5.45 : Types de pack
  VALID_PACK_TYPES,
  // Génération
  generateMissionId,
  createEmptyMissionData,
  // Clean Slate
  cleanSlateProtocol,
  clearPreviousData,
  // Persistence
  saveMissionData,
  loadMissionData,
  getActiveMissionId,
  getActiveMissionByPackType, // V5.45
  // Mise à jour
  updateCategoryStatus,
  updateCategoryTechnicalData,
  getActiveCategories,
  updateAddresses,
  updateCurrentStep,
  updateFeeDisclaimerConsent,
  prepareDataForBackend,
  // Validation
  validateStep1,
  validateStep2,
  validateStep3,
  validateBeforePayment
};
